import { graphql, useStaticQuery } from 'gatsby'
import React, { useState } from 'react'
import FaqCategoryQuestion, {
  generateFaqCategoryQuestionId,
} from 'src/components/molecules/faqCategoryQuestion'
import {
  PrismicFaqCategory,
  PrismicLinkType,
} from 'src/typings/generated/graphql'
import CustomLink from 'src/utils/customLink'
import { generateRandomId } from 'src/utils/domHelper'
import * as Styles from './faq.module.scss'

export type FaqContent = {
  anchor?: string
  headline?: string
}

type Props = {
  content: FaqContent
}

type FaqCategoryQueryNode = Pick<PrismicFaqCategory, 'id' | 'dataRaw'> & {
  data: Pick<PrismicFaqCategory['data'], 'title' | 'question_and_answer'>
}

type NormalFaqCategoriesQuery = {
  allPrismicFaqCategory: {
    nodes: FaqCategoryQueryNode[]
  }
}

const getExternalLink = (category: FaqCategoryQueryNode) => {
  // Use dataRaw until the external_link field is available in the generated schema.
  const dataRaw = category.dataRaw as {
    external_link?: PrismicLinkType
  }

  return dataRaw?.external_link?.url ? dataRaw.external_link : undefined
}

const Faq = ({ content }: Props) => {
  const [activeCategory, setActiveCategory] = useState(0)
  const [focusActiveQuestion, setFocusActiveQuestion] = useState(false)
  const generatedAnchor = generateRandomId()
  const anchor = content.anchor ?? generatedAnchor
  const { headline = '' } = content

  const categoriesQuery = useStaticQuery<NormalFaqCategoriesQuery>(graphql`
    query NormalFaqCategoriesQuery {
      allPrismicFaqCategory(sort: { data: { title: { text: ASC } } }) {
        nodes {
          id
          dataRaw
          data {
            title {
              text
            }
            question_and_answer {
              question {
                text
              }
              answer {
                richText
              }
            }
          }
        }
      }
    }
  `)

  const categories = categoriesQuery.allPrismicFaqCategory.nodes

  const categoryItems = categories.map(category => ({
    category,
    externalLink: getExternalLink(category),
  }))
  const localCategories = categoryItems
    .filter(({ externalLink }) => !externalLink)
    .map(({ category }) => category)
  const localCategoryIndexes = new Map(
    localCategories.map((category, categoryIndex) => [
      category.id,
      categoryIndex,
    ])
  )
  const questionsId = `${anchor}-questions`

  const updateActiveCategory = (categoryIndex: number) => {
    const category = localCategories[categoryIndex]
    if (!category) return

    setActiveCategory(categoryIndex)
    setFocusActiveQuestion(true)
  }

  const generateCategoryControls = () =>
    categoryItems.map(({ category, externalLink }) => {
      if (externalLink) {
        return (
          <CustomLink
            key={category.id}
            link={externalLink}
            className={Styles.category}
          >
            {category.data.title?.text ?? ''}
          </CustomLink>
        )
      }

      const categoryIndex = localCategoryIndexes.get(category.id)
      if (categoryIndex === undefined) return null

      const isActive = categoryIndex === activeCategory
      const categoryClass = `${Styles.category} ${
        isActive ? Styles.active : ''
      }`

      return (
        <button
          key={category.id}
          type="button"
          className={categoryClass}
          onClick={() => updateActiveCategory(categoryIndex)}
          aria-pressed={isActive}
          aria-controls={questionsId}
        >
          {category.data.title?.text}
        </button>
      )
    })

  const generateQuestions = () => {
    if (localCategories[activeCategory] === undefined) return ''

    const category = localCategories[activeCategory]

    return category.data.question_and_answer?.map((qa, i) => {
      if (!qa) return null

      const questionId = generateFaqCategoryQuestionId(category.id, i)

      return (
        <FaqCategoryQuestion
          id={questionId}
          question={qa}
          key={questionId}
          autoFocus={focusActiveQuestion && i === 0}
        />
      )
    })
  }

  return (
    <section id={anchor} className={Styles.faq}>
      <div className={Styles.contentContainer}>
        <div className={Styles.directory}>
          <h3 id={`${anchor}-categories-heading`}>{headline}</h3>
          <div role="group" aria-labelledby={`${anchor}-categories-heading`}>
            {generateCategoryControls()}
          </div>
        </div>
        {localCategories.length > 0 && (
          <div id={questionsId} className={Styles.questions}>
            {generateQuestions()}
          </div>
        )}
      </div>
    </section>
  )
}

export default Faq
